import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { useId, type ComponentProps, type ReactNode } from 'react';

import { cn } from './cn';
import { Label } from './label';

export type RadioGroupProps = ComponentProps<typeof RadioGroupPrimitive.Root> & { readonly readOnly?: boolean };

/** Grupo de escolha única (Radix). Setas movem entre as opções. */
export function RadioGroup({ className, readOnly, disabled, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      disabled={disabled === true || readOnly === true}
      className={cn('grid gap-2.5', className)}
      {...props}
    />
  );
}

export function RadioGroupItem({ className, ...props }: ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'peer relative inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-input bg-card',
        'data-[state=checked]:border-primary disabled:cursor-not-allowed disabled:opacity-45',
        'after:absolute after:-inset-4 md:after:-inset-2',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-primary" />
    </RadioGroupPrimitive.Item>
  );
}

export interface RadioOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}

/** Atalho: um `RadioGroup` com um item rotulado por opção. */
export function RadioOptions({ options, ...props }: RadioGroupProps & { readonly options: readonly RadioOption[] }) {
  const id = useId();
  return (
    <RadioGroup {...props}>
      {options.map((option) => {
        const itemId = `${id}-${option.value}`;
        return (
          <div key={option.value} className="flex items-start gap-2.5">
            <RadioGroupItem id={itemId} value={option.value} disabled={option.disabled === true} className="mt-0.5" />
            <div className="grid gap-0.5">
              <Label htmlFor={itemId} className="font-normal">
                {option.label}
              </Label>
              {option.description ? <p className="text-xs text-muted-foreground">{option.description}</p> : null}
            </div>
          </div>
        );
      })}
    </RadioGroup>
  );
}
